import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, PageBody } from "@/components/PageParts";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { fmtDate, fmtMoney } from "@/lib/format";
import { toast } from "sonner";

export default function Revenue() {
  const [schedules, setSchedules] = useState([]);
  const [postings, setPostings] = useState([]);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    const [s, p] = await Promise.all([api.get("/revenue/schedules"), api.get("/revenue/postings")]);
    setSchedules(s.data); setPostings(p.data);
  };
  useEffect(() => { load(); }, []);

  const postPeriod = async (sid, period) => {
    try {
      await api.post(`/revenue/schedules/${sid}/post-period?period=${period}`);
      toast.success(`Posted ${period}`);
      const { data } = await api.get("/revenue/schedules");
      setSchedules(data);
      const fresh = data.find((x) => x.id === sid);
      if (fresh) setSelected(fresh);
      const { data: postings2 } = await api.get("/revenue/postings"); setPostings(postings2);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
  };

  return (
    <>
      <PageHeader title="Revenue recognition" subtitle="EliteMind monthly schedules starting from the EliteBase onboarding month. Post period-by-period." />
      <PageBody>
        <Tabs defaultValue="schedules">
          <TabsList className="mb-4">
            <TabsTrigger value="schedules" data-testid="tab-schedules">Schedules</TabsTrigger>
            <TabsTrigger value="postings" data-testid="tab-postings">Postings</TabsTrigger>
          </TabsList>
          <TabsContent value="schedules">
            <div className="bg-white border border-slate-200 rounded-md shadow-sm overflow-hidden">
              <Table>
                <TableHeader><TableRow><TableHead>Sale</TableHead><TableHead>Onboarding</TableHead><TableHead className="text-right">Months</TableHead><TableHead className="text-right">Total</TableHead><TableHead className="text-right">Recognised</TableHead><TableHead>Source</TableHead></TableRow></TableHeader>
                <TableBody>
                  {schedules.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-slate-500 py-6">No revenue schedules yet.</TableCell></TableRow>}
                  {schedules.map((s) => (
                    <TableRow key={s.id} className="cursor-pointer" onClick={() => setSelected(s)} data-testid={`schedule-row-${s.id}`}>
                      <TableCell className="text-xs font-mono">{s.sale_id?.slice(0, 8)}…</TableCell>
                      <TableCell className="text-xs font-mono">{s.onboarding_month}</TableCell>
                      <TableCell className="text-right font-mono">{s.duration_months}</TableCell>
                      <TableCell className="text-right font-mono">{fmtMoney(s.total_minor, s.currency)}</TableCell>
                      <TableCell className="text-right font-mono text-emerald-700">{fmtMoney(s.recognised_minor || 0, s.currency)}</TableCell>
                      <TableCell className="text-xs">{s.source}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
          <TabsContent value="postings">
            <div className="bg-white border border-slate-200 rounded-md shadow-sm overflow-hidden">
              <Table>
                <TableHeader><TableRow><TableHead>Period</TableHead><TableHead>Schedule</TableHead><TableHead className="text-right">Amount</TableHead><TableHead>Posted at</TableHead></TableRow></TableHeader>
                <TableBody>
                  {postings.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-slate-500 py-6">No postings yet.</TableCell></TableRow>}
                  {postings.map((p) => (
                    <TableRow key={p.id}>
                      <TableCell className="text-xs font-mono">{p.period}</TableCell>
                      <TableCell className="text-xs font-mono">{p.schedule_id?.slice(0, 8)}…</TableCell>
                      <TableCell className="text-right font-mono">{fmtMoney(p.amount_minor, p.currency)}</TableCell>
                      <TableCell className="text-xs font-mono">{fmtDate(p.posted_at)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        </Tabs>
      </PageBody>

      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent className="min-w-[520px] overflow-y-auto">
          {selected && (
            <>
              <SheetHeader><SheetTitle className="font-mono">Schedule · {selected.onboarding_month}</SheetTitle></SheetHeader>
              <div className="mt-6 text-sm space-y-3">
                <div className="text-xs text-slate-500">Total {fmtMoney(selected.total_minor, selected.currency)} · {selected.duration_months} months</div>
                <div className="border rounded overflow-hidden">
                  <Table>
                    <TableHeader><TableRow><TableHead>Period</TableHead><TableHead className="text-right">Amount</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
                    <TableBody>
                      {selected.periods.map((p, i) => (
                        <TableRow key={`${p.period || "period"}-${i}`}>
                          <TableCell className="font-mono text-xs">{p.period}</TableCell>
                          <TableCell className="text-right font-mono">{fmtMoney(p.amount_minor, selected.currency)}</TableCell>
                          <TableCell><Badge variant="outline" className={p.status === "posted" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-50 text-slate-600 border-slate-200"}>{p.status}</Badge></TableCell>
                          <TableCell>
                            {p.status === "scheduled" && (
                              <Button size="sm" variant="outline" className="h-6 text-xs px-2" onClick={() => postPeriod(selected.id, p.period)} data-testid={`post-period-${p.period}`}>Post</Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </>
  );
}
